import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { teamAPI } from '../../teams/services';
import TeamList from '../../teams/components/TeamList';
import TeamDetail from '../../teams/components/TeamDetail';

/**
 * TeamsTab - Self-Contained Teams Management Component
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 *
 * Features:
 * - View teams for this squad (list and detail views)
 * - Create new teams (navigates to create page)
 * - Search teams
 * - Delete teams
 * - Edit teams (navigates to edit page)
 *
 * Architecture:
 * - Shows TeamList by default
 * - When clicking "View", shows inline team details (stays on /squad/ route)
 * - Edit/Create actions navigate to separate pages
 */

const TeamsTab = ({ voProvider }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Local state for selected team (detail view)
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeamData, setSelectedTeamData] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Guard: Return loading state if voProvider not available
  if (!voProvider) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 text-center">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  // Get data from VO
  const group = voProvider.getGroup();
  const groupId = voProvider.vo.groupId;

  /**
   * Fetch team details from API
   */
  const fetchTeamDetails = useCallback(async (teamId) => {
    setLoadingTeam(true);
    try {
      const result = await teamAPI.getById(teamId);
      const teamData = result?.data || result;
      setSelectedTeamData(teamData);
    } catch (error) {
      console.error('[TeamsTab] Error fetching team:', error);
      toast.error('Failed to load team details');
      setSelectedTeamId(null);
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  /**
   * Fetch team details when selectedTeamId changes
   */
  useEffect(() => {
    if (selectedTeamId) {
      fetchTeamDetails(selectedTeamId);
    } else {
      setSelectedTeamData(null);
    }
  }, [selectedTeamId, fetchTeamDetails]);

  /**
   * Handle team click - show team details inline
   */
  const handleTeamClick = useCallback((team) => {
    const teamId = team.id || team.teamId;
    setSelectedTeamId(teamId);
  }, []);

  /**
   * Handle back to teams list
   */
  const handleBackToList = useCallback(() => {
    setSelectedTeamId(null);
    setSelectedTeamData(null);
  }, []);

  /**
   * Handle edit team - navigate to edit page with return URL
   */
  const handleEditTeam = useCallback((team) => {
    const teamId = team.id || team.teamId;
    const returnUrl = `/squads/${groupId}?tab=teams`;
    navigate(`/squads/${groupId}/teams/${teamId}/edit?returnUrl=${encodeURIComponent(returnUrl)}`);
  }, [groupId, navigate]);

  /**
   * Handle create team - navigate to create page with return URL
   */
  const handleCreateTeam = useCallback(() => {
    const returnUrl = `/squads/${groupId}?tab=teams`;
    navigate(`/squads/${groupId}/teams/new?returnUrl=${encodeURIComponent(returnUrl)}`);
  }, [groupId, navigate]);

  // LIST VIEW: Show teams list
  if (!selectedTeamId) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
        <TeamList
          ownerType="group"
          ownerId={groupId}
          showCreateButton={true}
          showHeader={true}
          showSearch={true}
          title="Squad Teams"
          emptyMessage="No teams in this squad yet"
          emptyDescription="Create teams to organize your squad members for matches and tournaments"
          createButtonText="Create Team"
          baseUrl={`/squads/${groupId}/teams`}
          onTeamClick={handleTeamClick}
          onCreateClick={handleCreateTeam}
          onEditClick={handleEditTeam}
        />
      </div>
    );
  }

  // DETAIL VIEW: Show selected team details
  return (
    <div>
      {/* Back button */}
      <button
        onClick={handleBackToList}
        className={`flex items-center gap-x-2 mb-4 px-4 py-2 rounded-lg ${theme.backgroundSecondary} ${theme.text} hover:opacity-80 transition-opacity`}
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Teams</span>
      </button>

      {/* Loading state */}
      {loadingTeam && (
        <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
          <div className="flex flex-col items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-blue-500 mb-4" />
            <span className={theme.textMuted}>Loading team details...</span>
          </div>
        </div>
      )}

      {/* Team details using reusable TeamDetail component */}
      {!loadingTeam && selectedTeamData && (
        <TeamDetail
          team={selectedTeamData}
          showEditButton={true}
          onEdit={handleEditTeam}
        />
      )}
    </div>
  );
};

export default TeamsTab;
